import React, { useCallback, memo, useContext } from 'react';
import type {
  QuestionEntry,
  RichContent,
} from '@wix/ambassador-faq-question-entry-v2-question-entry/types';
import { Accordion, AccordionTheme } from '@wix/wix-ui-tpa/cssVars';
import { st, classes } from './accordion.st.css';
import SocialWrapper from '../../../SocialWrapper/SocialWrapper';
import RCV from '../../../RCV';
import { ItemAppearance, OpenQuestionIcon } from '../../../../settingsParams';
import { openIconBySettings } from '../../../../../../utils/openIconBySettings';
import {
  scrollToElement,
  SCROLL_DELAY_ANIMATED,
  SCROLL_DELAY_INSTANT,
} from '../../../../../../utils/scrollToElement';
import { AppContext } from '../../../SettingsProvider/SettingsProvider';
import { useBi, useEnvironment } from '@wix/yoshi-flow-editor';
import { faqQuestionExpanded } from '@wix/bi-logger-labs-data/v2';
import { DraftJSObject } from '../../../../../../types/RichContent';
import { DATA_HOOKS } from '../../../../../../constants/dataHooksElements';
import { areQuestionsEqual } from '../../../../../../hooks/useQuestions';

const DEFAULT_QUESTION_FONT = { htmlTag: 'h3' as keyof JSX.IntrinsicElements };

export interface AccordionOneColumnProps {
  filteredQuestions: QuestionEntry[];
  firstQuestionId?: string;
  baseURL?: string;
  noAnimation?: boolean;
  openOneAnswer?: boolean;
  layout_type?: string;
  itemAppearance?: ItemAppearance;
  openQuestionIcon?: OpenQuestionIcon;
  questionFontParam?: { htmlTag?: keyof JSX.IntrinsicElements };
}

const AccordionOneColumnComponent = ({
  filteredQuestions,
  firstQuestionId,
  baseURL,
  noAnimation = false,
  openOneAnswer = true,
  layout_type = 'collapse',
  itemAppearance = ItemAppearance.BOX,
  openQuestionIcon = OpenQuestionIcon.ARROW,
  questionFontParam = DEFAULT_QUESTION_FONT,
}: AccordionOneColumnProps) => {
  const logger = useBi();
  const { isMobile } = useEnvironment();
  const { scrollToQuestion } = useContext(AppContext);

  const accordionTheme: AccordionTheme =
    itemAppearance === ItemAppearance.BOX
      ? AccordionTheme.Box
      : AccordionTheme.Line;

  const icons = openIconBySettings(openQuestionIcon);

  const handleItemClick = useCallback(
    (itemId: string, isExpanded: boolean) => {
      if (!isExpanded) {
        return;
      }
      const questionData = filteredQuestions.find(
        (q: QuestionEntry) => q.id === itemId,
      );
      if (questionData) {
        const { question, categoryId, sortOrder } = questionData;
        logger.report(
          faqQuestionExpanded({
            type: layout_type,
            question_id: itemId,
            question_name: question || '',
            category_id: categoryId || '',
            question_order: sortOrder || 0,
            url: baseURL || '',
            isOoi: true,
          }),
        );
      }
      if (openOneAnswer) {
        scrollToElement(
          itemId,
          noAnimation ? SCROLL_DELAY_INSTANT : SCROLL_DELAY_ANIMATED,
          scrollToQuestion,
        );
      }
    },
    [
      filteredQuestions,
      layout_type,
      baseURL,
      logger,
      openOneAnswer,
      noAnimation,
      scrollToQuestion,
    ],
  );

  // Build the className for the accordion
  const stClassAccordion = st(classes.flexAccordion, {
    noAnimation,
    itemAppearance,
    mobile: isMobile,
  });

  // Early return if there are no questions to display
  if (!filteredQuestions.length) {
    return null;
  }

  return (
    <Accordion
      key={`accordion-${filteredQuestions.at(0)?.id}-${
        filteredQuestions.at(-1)?.id
      }`}
      className={st(classes.root, stClassAccordion)}
      icons={icons}
      multiple={!openOneAnswer}
      initiallyExpanded={firstQuestionId || ''}
      theme={accordionTheme}
      onItemClick={handleItemClick}
      data-hook={DATA_HOOKS.WIDGET_ACCORDION_ONE_COLUMN(
        filteredQuestions[0]?.id || '',
      )}
    >
      {filteredQuestions.map(
        ({ id, question, draftjs, plainText, richContent }) => {
          const content: DraftJSObject | RichContent | string = draftjs
            ? JSON.parse(draftjs)
            : richContent ?? plainText ?? '';

          return (
            <Accordion.Item
              key={id!}
              id={id!}
              title={question!}
              titleTag={questionFontParam.htmlTag}
            >
              {content && (
                <RCV convertAnswerToJSON={content} answerQuestionId={id!} />
              )}
              <SocialWrapper questionId={id!} />
            </Accordion.Item>
          );
        },
      )}
    </Accordion>
  );
};

const areAccordionPropsEqual = (
  prev: AccordionOneColumnProps,
  next: AccordionOneColumnProps,
): boolean =>
  areQuestionsEqual(prev.filteredQuestions, next.filteredQuestions) &&
  prev.firstQuestionId === next.firstQuestionId &&
  (prev.baseURL ?? '') === (next.baseURL ?? '') &&
  prev.noAnimation === next.noAnimation &&
  prev.openOneAnswer === next.openOneAnswer &&
  prev.layout_type === next.layout_type &&
  prev.itemAppearance === next.itemAppearance &&
  prev.openQuestionIcon === next.openQuestionIcon &&
  prev.questionFontParam?.htmlTag === next.questionFontParam?.htmlTag;

const AccordionOneColumn = memo(
  AccordionOneColumnComponent,
  areAccordionPropsEqual,
);
AccordionOneColumn.displayName = 'AccordionOneColumn';

export default AccordionOneColumn;
